import { Injectable, inject } from '@angular/core';
import { Route, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';

/**
 * Where a session lands, decided in one place.
 *
 * This used to be four places that grew case by case — App.ngOnInit, an OAuth
 * `state` branch, a never-called handleLoginSuccess(), and the route guard —
 * three of which quietly resolved to /dashboard. The visible symptom: sign in
 * from a link to /academic-officer/10000532/profile and you arrived on the
 * dashboard, because the callback URL carried `code=` and that was all
 * App.ngOnInit looked at.
 *
 * The contract:
 *  1. a remembered route, if it is still one the user may open;
 *  2. otherwise the user's home (PermissionService.homeRoute()).
 * A remembered route the user's roles no longer cover lands them home and says
 * so, rather than greeting a fresh sign-in with a refusal page.
 */
@Injectable({ providedIn: 'root' })
export class LandingService {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);
  private readonly snackBar = inject(MatSnackBar);

  /**
   * Land the session. Call once, after authentication has settled.
   *
   * Always replaces the current history entry: the entry being replaced is the
   * OAuth callback (/index.html?code=…), and Back must not return to it.
   */
  async land(): Promise<void> {
    const remembered = this.authService.consumeRememberedRoute();

    // The permission check below is only meaningful once GET /me/ has answered.
    // Without this wait the remembered route is judged against no roles at all,
    // which is how the deep link got lost in the first place.
    await this.permissionService.waitForRoles();

    if (remembered) {
      if (this.canOpen(remembered)) {
        console.log('🔖 Landing on the remembered route:', remembered);
        await this.router.navigateByUrl(remembered, { replaceUrl: true });
        return;
      }
      console.log('🔖 Remembered route is not permitted for these roles:', remembered);
      this.snackBar.open(
        "You don't have access to that page. Showing your home page instead.",
        'Dismiss',
        { duration: 8000, horizontalPosition: 'center', verticalPosition: 'top' },
      );
    }

    const home = this.permissionService.homeRoute();
    console.log('🔖 Landing on home:', home);
    await this.router.navigateByUrl(home, { replaceUrl: true });
  }

  /**
   * Whether the user's current permission covers a URL.
   *
   * Deliberately asks the same question the route guard asks
   * (`canAccessRoute`, i.e. the permission being acted as), so the landing
   * decision and the guard can never disagree and bounce the user twice.
   */
  private canOpen(url: string): boolean {
    const requiredGroups = this.requiredGroupsFor(url);
    if (requiredGroups === null) return false; // matches no route — home is kinder
    return this.permissionService.canAccessRoute(requiredGroups);
  }

  /**
   * The `requiredGroups` declared by the route a URL resolves to, or null when
   * no route matches. Reads the router config directly — `data` is static, so
   * this needs no lazy chunk to have loaded.
   */
  private requiredGroupsFor(url: string): string[] | null {
    const path = url.split('?')[0].split('#')[0].replace(/^\/+/, '');
    const segments = path.split('/').filter(Boolean);

    for (const route of this.router.config) {
      if (route.path === undefined || route.path === '**') continue;
      if (this.matches(route, segments)) {
        return (route.data?.['requiredGroups'] as string[] | undefined) ?? [];
      }
    }
    return null;
  }

  /** Segment-wise match, treating `:param` as a wildcard. Routes here are flat. */
  private matches(route: Route, segments: string[]): boolean {
    const routeSegments = (route.path ?? '').split('/').filter(Boolean);
    if (routeSegments.length !== segments.length) return false;
    return routeSegments.every(
      (segment, i) => segment.startsWith(':') || segment === segments[i],
    );
  }
}
